/**
 * Вогняний снаряд: a stone wrapped in linen and soaked in смола.
 *
 * Thrown by a требушет, not by hand. It arcs, it is slow enough to watch, and
 * where it lands it sets things alight - which is the whole reason the thing
 * exists, and the answer to a question this game has been carrying since the
 * блоки first got a `burnRate` nobody read.
 *
 * **Not an arrow.** An arrow is fast, flat and sticks where it lands; this is
 * heavy, lobbed, and gone the instant it touches anything. Sharing the arrow
 * code would have meant an `isFireball` flag inside every branch of it, which
 * is how two things that are not the same end up pretending to be.
 *
 * Pure: a position, a velocity and a solidity test. What catching fire *means*
 * belongs to core/world/fire.ts and what to draw belongs to render/.
 */

import type { SolidTest } from "../physics/voxelCollision.js";

/**
 * Blocks a second as it leaves the arm.
 *
 * Slow next to an arrow's forty-two: a trebuchet lobs, and a shot you can
 * watch travel is a shot you can aim by watching the last one land.
 */
export const BOLT_SPEED = 22;
/** Heavier than an arrow, so it drops out of the sky rather than sailing. */
export const BOLT_GRAVITY = 26;

/**
 * What a требушет throws at, which is further than an arm can.
 *
 * "Зроби щоб требушет стріляв вдвічі далі." Twice as far is not twice as
 * fast: a thing thrown at forty-five degrees goes v² over g, so the speed
 * goes up by the square root of two and the distance doubles. Doubling the
 * speed would have quadrupled it.
 *
 * Its own number rather than a bigger `BOLT_SPEED`, because a Змій breathes
 * at that one and his range is a fact about him.
 */
/**
 * And a fifth further again, twice over. "Додай дальность, давай 20
 * відсотків", and then "зроби щоб він стріляв ще на 20 відсотків далі".
 *
 * Range goes as the *square* of the speed - `throwRange` is v² over g - so a
 * fifth more ground is the square root of 1.2 more speed, not a fifth more
 * speed. Written as the range, because the range is the thing anybody can
 * see: the day it wants another fifth, this is the number to change.
 */
export const TREBUCHET_REACH = 1.2 * 1.2;

export const TREBUCHET_SPEED = BOLT_SPEED * Math.SQRT2 * Math.sqrt(TREBUCHET_REACH);

/**
 * The slowest a требушет will throw, against the fastest.
 *
 * "Щоб можна було регулювати силу та висоту кидка." The engine threw one
 * distance, at forty-five degrees, in whichever of four directions it had
 * been built facing - which is a machine with no controls at all. Now the
 * throw is aimed the way you are looking and wound like a bow: hold to wind,
 * let go to throw.
 *
 * The full wind is what it always threw at, so «вдвічі далі» is still what a
 * fully wound engine does. The slow end is a third of that - enough to drop
 * a bolt over your own wall without sending it into the sea.
 */
export const TREBUCHET_MIN_SPEED = TREBUCHET_SPEED * 0.55;

/** Seconds of holding it down to wind the arm all the way. */
export const WIND_SECONDS = 2.2;

/** How far a wind of this much gets, 0..1. */
export function windSpeed(power: number): number {
  const wound = Math.max(0, Math.min(1, power));
  return TREBUCHET_MIN_SPEED + (TREBUCHET_SPEED - TREBUCHET_MIN_SPEED) * wound;
}

/**
 * Where a wound требушет throws, given where the player is looking.
 *
 * Direction *and* elevation come off the look: the yaw is where it goes and
 * the pitch is how high it is lobbed, which is the two halves of aiming a
 * thing that arcs. Straight down is refused - an engine does not throw into
 * its own bed - and anything below the flat is flattened to the flat.
 *
 * `look` need not be normalised; what matters is its direction.
 */
export function trebuchetThrow(
  look: { x: number; y: number; z: number }, power: number,
): { x: number; y: number; z: number } {
  const flat = Math.hypot(look.x, look.z);
  // Looking straight up or straight down: throw it over the front, steeply.
  const outX = flat < 1e-3 ? 0 : look.x / flat;
  const outZ = flat < 1e-3 ? -1 : look.z / flat;
  /*
   * The angle, in the same units the aim is in, and never below the flat.
   *
   * A trebuchet lobs. Aiming at your own feet would otherwise hand you a
   * bolt in the face, and there is no shot it would be the right answer to.
   */
  const up = Math.max(0, look.y / Math.hypot(look.x, look.y, look.z));
  const along = Math.sqrt(Math.max(0, 1 - up * up));
  const speed = windSpeed(power);
  return { x: outX * along * speed, y: up * speed, z: outZ * along * speed };
}

/** How far a thing thrown at forty-five degrees lands from where it left. */
export function throwRange(speed: number): number {
  return (speed * speed) / BOLT_GRAVITY;
}
/** Gone after this long, in case it is loosed at the sky. */
export const BOLT_LIFETIME = 12;
/** What it does to whatever it lands on. */
export const BOLT_DAMAGE = 6;

export interface Firebolt {
  readonly id: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Seconds it has been in the air. */
  age: number;
  /**
   * Cold fire: blue, and it does not set anything alight.
   *
   * What Чахлик throws. It burns whoever it hits and leaves the timber
   * standing, which is the difference between a weapon and a siege - and the
   * reason his own замок is still there after a fight in it. A трeбушет's
   * bolt is the other kind: a stone in burning смола, and what it is *for*
   * is the fire it starts.
   */
  readonly cold: boolean;
  /**
   * A Змій's breath rather than a thrown stone.
   *
   * The same flight and the same fire at the end of it; what differs is
   * the picture - a ball of live flame the size of a head, not a tumbling
   * lump of burning смола.
   */
  readonly breath: boolean;
}

let nextId = 1;

export function makeFirebolt(
  from: { x: number; y: number; z: number },
  dir: { x: number; y: number; z: number },
  speed = BOLT_SPEED,
  cold = false,
  breath = false,
): Firebolt {
  return {
    id: nextId++,
    x: from.x, y: from.y, z: from.z,
    vx: dir.x * speed, vy: dir.y * speed, vz: dir.z * speed,
    age: 0,
    cold,
    breath,
  };
}

/**
 * Синій вогонь: how fast and how hard the cold kind flies.
 *
 * Faster and flatter than a trebuchet's lob, because it is thrown at a
 * person rather than at a wall - but slower than an arrow, so a ball of it
 * coming across a hall is something you can see and step out of. That is the
 * whole of the fight: he is slow, his fire is not, and the room is where you
 * find the pillar to put between you.
 */
export const COLD_SPEED = 30;
export const COLD_GRAVITY = 9;
/** What it takes off whoever it catches. */
export const COLD_DAMAGE = 9;

/**
 * The velocity that lands a bolt on a given point.
 *
 * "Дрон дуже погано попадає, його граната летить далеко від приціла." It was
 * not aimed at all: the drone let its снаряд go with its own speed and a
 * nudge downwards, so it landed under the drone and the crosshair had
 * nothing to do with it. Leading the target by eye is a fine skill for a
 * трeбушет, whose arm cannot be turned; it is not one for a thing with a
 * camera and a crosshair in the middle of it.
 *
 * Two arcs, and which one it is depends on whether falling gets there in
 * time:
 *
 *   - Looking down at something below, the bolt is simply *let go* forward:
 *     the fall decides the flight time and the horizontal speed follows from
 *     it. This is the ordinary case and it is the one that feels right -
 *     nothing is thrown, the drone drops it and it arrives.
 *   - Looking further out than a fall can carry it, the bolt is lobbed: the
 *     horizontal speed is capped at `flat` and the arc is raised to suit.
 *
 * @returns null when there is no arc at all - a target directly above, with
 * nowhere for it to go.
 */
export function aimedThrow(
  from: { x: number; y: number; z: number },
  to: { x: number; y: number; z: number },
  gravity = BOLT_GRAVITY,
  flat = BOLT_SPEED,
): { x: number; y: number; z: number } | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const out = Math.hypot(dx, dz);
  const drop = -dy;
  const falling = drop > 0 ? Math.sqrt((2 * drop) / gravity) : 0;
  // Fast enough to be there when it lands? Then let it fall, and no faster.
  const t = (falling > 0 && out <= flat * falling) ? falling : out / flat;
  if (!(t > 0)) return null;
  return { x: dx / t, y: (dy + 0.5 * gravity * t * t) / t, z: dz / t };
}

/** Where a bolt came down, and what it hit. */
export interface BoltImpact {
  readonly bolt: Firebolt;
  /** The cell it struck. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * Fly them, and report where they landed.
 *
 * Stepped in small pieces rather than one leap a frame: at twenty-two blocks
 * a second a sixtieth of a second is a third of a block, and a frame that
 * stutters would otherwise put a bolt straight through a wall. The same trap
 * the arrows have, answered the same way.
 */
export function stepFirebolts(
  bolts: Firebolt[],
  dt: number,
  isSolid: SolidTest,
): BoltImpact[] {
  const impacts: BoltImpact[] = [];

  for (let i = bolts.length - 1; i >= 0; i--) {
    const bolt = bolts[i]!;
    bolt.age += dt;
    if (bolt.age > BOLT_LIFETIME) {
      bolts.splice(i, 1);
      continue;
    }

    // Cold fire is thrown at a person and falls off far less than a stone
    // lobbed at a wall does.
    bolt.vy -= (bolt.cold ? COLD_GRAVITY : BOLT_GRAVITY) * dt;

    const speed = Math.hypot(bolt.vx, bolt.vy, bolt.vz);
    const steps = Math.max(1, Math.ceil((speed * dt) / 0.25));
    const slice = dt / steps;
    let landed = false;

    for (let s = 0; s < steps && !landed; s++) {
      bolt.x += bolt.vx * slice;
      bolt.y += bolt.vy * slice;
      bolt.z += bolt.vz * slice;
      const cx = Math.floor(bolt.x);
      const cy = Math.floor(bolt.y);
      const cz = Math.floor(bolt.z);
      if (isSolid(cx, cy, cz)) {
        impacts.push({ bolt, x: cx, y: cy, z: cz });
        bolts.splice(i, 1);
        landed = true;
      }
    }
  }

  return impacts;
}
